import React, { useEffect, useRef, useState } from "react";
import {
    Avatar, Box, Chip, CircularProgress, Dialog, Divider,
    IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Tooltip, Typography,
} from "@mui/material";
import CheckCircleRoundedIcon   from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon        from "@mui/icons-material/CancelRounded";
import PauseCircleRoundedIcon   from "@mui/icons-material/PauseCircleRounded";
import CheckRoundedIcon         from "@mui/icons-material/CheckRounded";
import PauseRoundedIcon         from "@mui/icons-material/PauseRounded";
import DownloadRoundedIcon      from "@mui/icons-material/DownloadRounded";
import CloseRoundedIcon         from "@mui/icons-material/CloseRounded";
import AttachFileRoundedIcon    from "@mui/icons-material/AttachFileRounded";
import ZoomInRoundedIcon        from "@mui/icons-material/ZoomInRounded";
import ChevronRightRoundedIcon  from "@mui/icons-material/ChevronRightRounded";
import UndoRoundedIcon          from "@mui/icons-material/UndoRounded";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { approvalApi } from "./api/approvalApi";
import AttachmentPreviewModal from "../mail/components/AttachmentPreviewModal";
import { isPreviewable } from "../../component/common/AttachmentPreviewList";
import {
    LINE_STATUS_META, STATUS_META, fileIcon, formatSize, formLabel, formColor,
} from "./approvalConstants";
import { runApiAction, serverMsgOr } from "../../util/apiFeedback";
import { confirm as confirmDialog } from "../../util/confirmDialog";
import ActionButton from "../../component/common/ActionButton";
import ComboBox from "../../component/common/ComboBox";
import FormField from "../../component/common/FormField";
import { ds } from "../../styles/designSystem";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ApprovalComments from "./ApprovalComments";
import { isVisible } from "./create/DynamicForm";
import {
    fieldValueText, isLongField, filledFields, legacyBodyText, LEGACY_BODY_LABEL,
} from "./formFieldText";

dayjs.locale("ko");

/* ── StatusBadge ─────────────────────────────────────── */
function StatusBadge({ status }) {
    const m = STATUS_META[status] || STATUS_META.PENDING;
    return (
        <Box sx={{
            display: "inline-flex", alignItems: "center",
            px: 1, py: 0.2, borderRadius: 1,
            bgcolor: m.bg, color: m.color, fontSize: 12, fontWeight: 700,
        }}>
            {m.label}
        </Box>
    );
}

/* ── SectionCard — 헤더 밴드 + 본문 (레퍼런스 카드 톤) ── */
function SectionCard({ title, action, children, bodySx, sx }) {
    return (
        <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden", bgcolor: "#fff", ...sx }}>
            {title && (
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: 2, py: 1.15, bgcolor: "#fafbfc", borderBottom: "1px solid #eef2f6",
                }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#475569", letterSpacing: "0.01em" }}>
                        {title}
                    </Typography>
                    {action}
                </Box>
            )}
            <Box sx={{ p: 2, ...bodySx }}>{children}</Box>
        </Box>
    );
}

/* ── SourceApprovalRef — 대체휴무의 근거가 된 대체근무(휴일근무) 결재 미리보기 + 이동 ── */
function SourceApprovalRef({ ids = [], text, onOpen }) {
    const [items, setItems]     = useState([]);
    const [loading, setLoading] = useState(false);

    const idList = Array.isArray(ids) ? ids.filter(Boolean) : [];

    useEffect(() => {
        if (!idList.length) { setItems([]); return; }
        let cancelled = false;
        setLoading(true);
        Promise.all(idList.map((id) =>
            approvalApi.detail(id)
                .then((r) => { const a = r?.data ?? r; return { id, title: a?.title, type: a?.type, status: a?.status, form: a?.form }; })
                .catch(() => ({ id, missing: true }))
        ))
            .then((res) => { if (!cancelled) setItems(res); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(idList)]);

    /* 근거 결재 부제: 유형 · 근무일 · 시간 */
    const subLine = (it) => {
        const d = it.form || {};
        const typeLabel = formLabel(it);
        const when = d.date ? `${dayjs(d.date).format("YYYY.MM.DD")}${d.hours ? ` · ${d.hours}시간` : ""}` : "";
        return [typeLabel, when].filter(Boolean).join(" · ");
    };

    return (
        <Box sx={{ py: 1.1, borderBottom: "1px solid #f1f5f9", "&:last-child": { borderBottom: "none" } }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#64748b", mb: 0.6 }}>대체 근무 결재 근거</Typography>

            {loading && <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>불러오는 중…</Typography>}

            {!loading && !idList.length && (
                <Typography sx={{ fontSize: 14, color: "#334155" }}>{text || "—"}</Typography>
            )}

            {!loading && idList.length > 0 && (
                <Stack spacing={0.75}>
                    {items.map((it) => (
                        <Box key={it.id} sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            px: 1.25, py: 0.9, borderRadius: 1.5,
                            border: "1px solid #e2e8f0", bgcolor: "#f8fafc",
                        }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }} noWrap>
                                    {it.missing ? `결재 #${it.id} · 조회 불가` : (it.title || `결재 #${it.id}`)}
                                </Typography>
                                {!it.missing && subLine(it) && (
                                    <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.1 }} noWrap>{subLine(it)}</Typography>
                                )}
                            </Box>
                            {onOpen && !it.missing && (
                                <ActionButton variant="secondary"
                                    onClick={() => onOpen(it.id)}
                                    startIcon={<ChevronRightRoundedIcon sx={{ fontSize: 16 }} />}
                                    sx={{ flexShrink: 0, fontSize: 12, py: 0.4, px: 1 }}
                                    label="결재로 이동" />
                            )}
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

/* ── FormFields — 원본 결재 양식과 같은 «라벨칸 | 값칸» 표 ─────────────────
 *
 * 비즈메카에서 쓰던 결재 양식이 전부 이 모양이다 — 회색 라벨칸이 왼쪽, 흰 값칸이 오른쪽,
 * 셀마다 실선 테두리. 현장에서 몇 년을 그 모양으로 봐 왔으므로 상세도 같은 형태로 그린다.
 * 라벨칸 폭을 고정해 어느 유형이든 값이 같은 자리에서 시작하게 한다.
 */
const LABEL_W = 168;

/**
 * 값이 주소면 링크로 — 아니면 문자열 그대로.
 *
 * 유형별 렌더러의 «구매처» 가 하던 일이다. 물품 구매 요청서는 구매처를 URL 로 적는 게 일상이라,
 * 동적 렌더링으로 옮기면서 이걸 빼면 «클릭이 안 된다» 는 체감 회귀가 된다.
 */
const URL_LIKE = /^(https?:\/\/\S+|[\w.-]+\.[a-z]{2,}([/?#]\S*)?)$/i;

function linkify(text) {
    const v = String(text || "").trim();
    if (!v || !URL_LIKE.test(v)) return text;
    return (
        <Box component="a"
            href={/^https?:\/\//i.test(v) ? v : `https://${v}`}
            target="_blank" rel="noreferrer"
            sx={{ fontSize: 14, fontWeight: 600, color: "#2563eb", textDecoration: "none",
                wordBreak: "break-all", "&:hover": { textDecoration: "underline" } }}>
            {v}
        </Box>
    );
}

const cellBase = {
    px: 1.5, py: 1.05,
    borderBottom: "1px solid #e2e8f0",
    display: "flex", alignItems: "center",
    minHeight: 40,
};

function FormFields({ type, form, fields, values, onOpenApproval, applicant }) {
    const data = form || {};

    /* 한 줄짜리 값 — 라벨칸 | 값칸 */
    const Row = (label, value) => (
        <Box key={label} sx={{ display: "flex", "&:last-of-type > *": { borderBottom: "none" } }}>
            <Box sx={{
                ...cellBase,
                width: LABEL_W, flexShrink: 0,
                bgcolor: "#f8fafc", borderRight: "1px solid #e2e8f0",
            }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#475569", lineHeight: 1.4 }}>
                    {label}
                </Typography>
            </Box>
            <Box sx={{ ...cellBase, flex: 1, minWidth: 0 }}>
                {(typeof value === "string" || typeof value === "number")
                    ? <Typography sx={{ fontSize: 14, color: "#1e293b", wordBreak: "break-word" }}>{value || "—"}</Typography>
                    : (value || <Typography sx={{ fontSize: 14, color: "#94a3b8" }}>—</Typography>)}
            </Box>
        </Box>
    );

    /* 긴 본문(사유·내용) — 같은 표 안에서 값칸만 세로로 넉넉하게 */
    const MultiRow = (label, value) => (
        <Box key={label} sx={{ display: "flex", "&:last-of-type > *": { borderBottom: "none" } }}>
            <Box sx={{
                ...cellBase,
                width: LABEL_W, flexShrink: 0, alignItems: "flex-start",
                bgcolor: "#f8fafc", borderRight: "1px solid #e2e8f0",
            }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#475569", lineHeight: 1.4 }}>
                    {label}
                </Typography>
            </Box>
            <Box sx={{ ...cellBase, flex: 1, minWidth: 0, alignItems: "flex-start" }}>
                <Typography sx={{
                    fontSize: 14, color: "#334155", whiteSpace: "pre-wrap",
                    wordBreak: "break-word", lineHeight: 1.65,
                }}>{value || "—"}
                </Typography>
            </Box>
        </Box>
    );

    const toComma = (v) => {
        const num = String(v || "").replace(/,/g, "").replace(/[^\d]/g, "");
        if (!num) return "";
        return Number(num).toLocaleString("ko-KR");
    };

    const fmtDatetime = (v) => v ? dayjs(v).format("YYYY.MM.DD HH:mm") : "—";
    const fmtDate     = (v) => v ? dayjs(v).format("YYYY.MM.DD") : "—";

    const durationStr = (start, end) => {
        if (!start || !end) return null;
        const mins = dayjs(end).diff(dayjs(start), "minute");
        if (mins <= 0) return null;
        const days  = Math.floor(mins / 1440);
        const hours = Math.floor((mins % 1440) / 60);
        const rem   = mins % 60;
        const parts = [];
        if (days  > 0) parts.push(`${days}일`);
        if (hours > 0) parts.push(`${hours}시간`);
        if (rem   > 0) parts.push(`${rem}분`);
        return parts.join(" ");
    };

    const summaryBox = (text, color = "#7e22ce", bg = "#fdf4ff", border = "#bfdbfe") => (
        <Box sx={{ mt: 1.25, px: 1.5, py: 1, bgcolor: bg, borderRadius: 1.5, border: `1px solid ${border}` }}>
            <Typography sx={{ fontSize: 12.5, color, fontWeight: 700 }}>{text}</Typography>
        </Box>
    );

    const applicantRow = Row("신청자", (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: "#2563eb" }}>
                {applicant?.[0] || "?"}
            </Avatar>
            <Typography sx={{ fontSize: 14, color: "#1e293b", fontWeight: 600 }}>
                {applicant || "—"}
            </Typography>
        </Box>
    ));

    /**
     * 양식 정의가 실려 오면 그 정의대로 그린다. 아래 유형별 분기는 <b>정의가 없는 옛 문서 전용</b>이다.
     *
     * 유형별 분기는 결재 유형 7개에 맞춰 손으로 짠 것이라, 양식이 만든 칸을 모른다. 업무보고서는
     * 유형이 OTHER 라 «제목 · 내용» 두 칸만 그렸고, 정작 사용자가 쓴 오전/오후 업무는 어느 칸에도
     * 안 걸려 문서가 통째로 비어 보였다. 정의를 받으면 라벨·순서·타입이 전부 문서에서 오므로
     * 관리자가 칸을 추가해도 프론트 배포 없이 그대로 나온다 — 상신 화면(DynamicForm)과 같은 구조다.
     *
     * 그리는 규칙 세 가지:
     * - 보임/숨김은 상신 화면과 <b>같은 함수</b>(isVisible)로 판단한다. 규칙을 두 번 구현하면
     *   «올릴 때 없던 칸» 이 상세에서만 «—» 로 나타난다.
     * - HIDDEN·DERIVED 칸도 그린다. 상신 때 숨긴 이유는 «사람이 고칠 값이 아니라서» 지
     *   «보면 안 되는 값» 이 아니다 — 차감 시간·고정된 휴가 종류는 결재자가 봐야 하는 값이다.
     * - <b>빈 칸은 행 자체를 안 그린다</b>(filledFields). 읽기 전용 화면에서 빈 행은 알려주는 게
     *   없는데, 옛 문서용으로 남겨 둔 «본문» 칸까지 겹쳐 «—» 만 열 줄씩 늘어선다.
     *
     * <p>한 칸도 안 걸리면 아래 옛 유형별 렌더러로 떨어진다 — 개편 전 문서는 값이 승격 컬럼에만
     * 남아 있을 수 있고, 그때는 «내용 없는 문서» 대신 옛 모양으로라도 보여주는 쪽이 맞다.
     */
    if (fields?.length) {
        const v = values || {};
        const shown = filledFields(fields.filter((f) => isVisible(f, v)), v);
        // 옛 문서의 본문(지급 내용·구매 사유…)은 지금 양식에 담을 칸이 없어 승격 컬럼에만 남는다.
        const legacyBody = legacyBodyText(type, data, shown.map((f) => fieldValueText(f, v[f.key])));

        if (shown.length || legacyBody) {
            return (
                <Box>
                    {applicantRow}
                    {shown.map((f) => {
                        const label = f.label || f.key;
                        const text = fieldValueText(f, v[f.key]);
                        // 라벨은 관리자가 자유롭게 정해 중복될 수 있다 — 키는 불변인 f.key 로 잡는다.
                        return (
                            <React.Fragment key={f.key}>
                                {isLongField(f) ? MultiRow(label, text) : Row(label, linkify(text))}
                            </React.Fragment>
                        );
                    })}
                    {legacyBody && MultiRow(LEGACY_BODY_LABEL, legacyBody)}
                </Box>
            );
        }
    }

    switch (type) {
        case "VACATION": {
            const vt = data.vacationType;
            const isHalfDay  = vt === "반차";
            const isCompDay  = vt === "대체휴무";
            const isHourly   = vt === "시간연차";
            const isDatetime = vt && vt !== "연차" && !isHalfDay && !isCompDay && !isHourly;
            const dur = isDatetime ? durationStr(data.startDate, data.endDate) : null;
            return (
                <Box>
                    {applicantRow}
                    {Row("휴가 종류", vt)}
                    {isHalfDay ? (
                        <>
                            {Row("날짜", fmtDate(data.startDate))}
                            {Row("반차 구분", data.halfDayType)}
                        </>
                    ) : isCompDay ? (
                        <>
                            {Row("보상 방식", data.redeemType === "CASH" ? "현금 정산" : "휴가 사용")}
                            {Row("사용 단위", data.unit)}
                            {data.redeemType === "CASH"
                                ? Row("정산 금액", data.hours === 4 ? "50,000원" : "100,000원")
                                : Row("날짜", fmtDate(data.startDate))}
                            {(data.sourceApprovalIds?.length > 0 || data.sourceApprovalText) && (
                                <SourceApprovalRef key="src-ref" ids={data.sourceApprovalIds}
                                    text={data.sourceApprovalText} onOpen={onOpenApproval} />
                            )}
                        </>
                    ) : isHourly ? (
                        <>
                            {Row("날짜", fmtDate(data.startDate))}
                            {Row("시작 시간", data.startTime)}
                            {Row("종료 시간", data.endTime)}
                        </>
                    ) : isDatetime ? (
                        <>
                            {Row("시작 일시", fmtDatetime(data.startDate))}
                            {Row("종료 일시", fmtDatetime(data.endDate))}
                        </>
                    ) : (
                        <>
                            {Row("시작일", fmtDate(data.startDate))}
                            {Row("종료일", fmtDate(data.endDate))}
                        </>
                    )}
                    {isHourly && data.hours > 0 && summaryBox(
                        `${fmtDate(data.startDate)} ${data.startTime || ""}~${data.endTime || ""} · ${data.hours}시간 연차 사용`,
                        "#0369a1", "#f0f9ff", "#bae6fd"
                    )}
                    {dur && summaryBox(
                        `${fmtDatetime(data.startDate)} ~ ${fmtDatetime(data.endDate)}  ·  ${dur}`,
                        "#0369a1", "#f0f9ff", "#bae6fd"
                    )}
                    {MultiRow("사유", data.reason)}
                </Box>
            );
        }
        case "PAYMENT":
            return (
                <Box>
                    {applicantRow}
                    {Row("거래처명", data.vendor)}
                    {Row("금액 (원)", data.amount ? `${toComma(data.amount)} 원` : "")}
                    {Row("지급 예정일", data.paymentDate)}
                    {MultiRow("지급 내용", data.description)}
                </Box>
            );
        case "PURCHASE":
            return (
                <Box>
                    {applicantRow}
                    {Row("품목명", data.item)}
                    {Row("수량", data.quantity)}
                    {Row("금액 (원)", data.amount ? `${toComma(data.amount)} 원` : "")}
                    {MultiRow("구매 사유", data.reason)}
                    {(data.vendorUrl || data.vendorName) && (() => {
                        const v = data.vendorUrl || data.vendorName;            // 통합 필드(vendorUrl) 우선, 옛 데이터는 vendorName 폴백
                        const isUrl = /^https?:\/\//i.test(v) || /^[\w.-]+\.[a-z]{2,}([/?#].*)?$/i.test(String(v).trim());
                        return Row("구매처", isUrl ? (
                            <Box component="a"
                                href={/^https?:\/\//i.test(v) ? v : `https://${v}`}
                                target="_blank" rel="noreferrer"
                                sx={{ fontSize: 14, fontWeight: 600, color: "#2563eb", textDecoration: "none",
                                    wordBreak: "break-all", "&:hover": { textDecoration: "underline" } }}>
                                {v}
                            </Box>
                        ) : v);
                    })()}
                </Box>
            );
        case "BUSINESS_TRIP": {
            const btDur = durationStr(data.startDate, data.endDate);
            return (
                <Box>
                    {applicantRow}
                    {Row("출장지", data.destination)}
                    {Row("출발 일시", fmtDatetime(data.startDate))}
                    {Row("복귀 일시", fmtDatetime(data.endDate))}
                    {Row("예상 비용 (원)", data.cost ? `${toComma(data.cost)} 원` : "")}
                    {btDur && summaryBox(`${fmtDatetime(data.startDate)} ~ ${fmtDatetime(data.endDate)}  ·  ${btDur}`)}
                    {MultiRow("출장 목적", data.purpose)}
                </Box>
            );
        }
        case "OVERTIME": {
            const otDur = data.date && data.startTime && data.endTime
                ? durationStr(`${data.date}T${data.startTime}`, `${data.date}T${data.endTime}`)
                : null;
            return (
                <Box>
                    {applicantRow}
                    {Row("날짜", fmtDate(data.date))}
                    {Row("시작 시간", data.startTime)}
                    {Row("종료 시간", data.endTime)}
                    {otDur && summaryBox(
                        `${data.date}  ${data.startTime} – ${data.endTime}  ·  ${otDur}`,
                        "#be123c", "#fff1f2", "#fecdd3"
                    )}
                    {MultiRow("사유", data.reason)}
                </Box>
            );
        }
        case "HOLIDAY_WORK": {
            const hrs = Number(data.hours) || 0;
            return (
                <Box>
                    {applicantRow}
                    {Row("근무일", fmtDate(data.date))}
                    {Row("시작 시간", data.startTime)}
                    {Row("종료 시간", data.endTime)}
                    {hrs > 0 && Row("실 근무시간", `${hrs}시간`)}
                    {hrs > 0 && Row("적립 대체휴무", hrs >= 8 ? "1일 (8시간)" : "적립 없음 (8시간 미만)")}
                    {MultiRow("사유", data.reason)}
                </Box>
            );
        }
        default:
            return (
                <Box>
                    {applicantRow}
                    {Row("제목", data.title)}
                    {MultiRow("내용", data.content)}
                </Box>
            );
    }
}

/* ── ApprovalLineRail — 세로 진행선 (레퍼런스 스텝퍼) ── */
function ApprovalLineRail({ lines = [], approval }) {
    if (!lines?.length) {
        return <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>결재 라인이 없습니다.</Typography>;
    }

    const join = (...parts) => parts.filter(Boolean).join(" · ");

    const submitterNode = {
        label: approval?.submitterName || "-",
        sub: join(approval?.submitterPosition, approval?.submitterDept),
        subLabel: "신청",
        status: "SUBMIT",
        initial: approval?.submitterName?.[0] || "?",
        color: "#2563eb",
        bgColor: "#2563eb",
    };

    const nodes = [
        submitterNode,
        ...lines.map((line, idx) => {
            const meta = LINE_STATUS_META[line.status] || LINE_STATUS_META.WAITING;
            const bgColor =
                line.status === "APPROVED" ? "#16a34a" :
                line.status === "REJECTED" ? "#dc2626" :
                line.status === "HELD"     ? "#d97706" : "#2563eb";
            return {
                label: line.approverName || "-",
                sub: join(line.approverPosition, line.approverDept),
                subLabel: meta.label,
                status: line.status,
                initial: line.approverName?.[0] || "?",
                color: meta.color,
                bgColor,
                isFinal: idx === lines.length - 1,
                isActive: line.status === "PENDING",
                comment: line.comment,
                actionAt: line.actionAt,
            };
        }),
    ];

    const circleSx = (node) => ({
        width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, fontWeight: 700,
        bgcolor: node.status === "WAITING" ? "#f1f5f9" : node.bgColor,
        color:   node.status === "WAITING" ? "#94a3b8" : "#fff",
        border:  node.isActive ? "2px solid #bfdbfe" : "2px solid transparent",
        boxShadow: node.isActive ? "0 0 0 4px rgba(37,99,235,0.12)" : "none",
    });

    const segBorder = (status) =>
        status === "APPROVED" ? "2px solid #22c55e" :
        status === "SUBMIT"   ? "2px solid #93c5fd" :
        "2px dashed #cbd5e1";

    const circleIcon = (status) =>
        status === "APPROVED" ? <CheckRoundedIcon sx={{ fontSize: 20 }} /> :
        status === "REJECTED" ? <CloseRoundedIcon sx={{ fontSize: 20 }} /> :
        status === "HELD"     ? <PauseRoundedIcon sx={{ fontSize: 20 }} /> : null;

    return (
        <Box>
            {nodes.map((node, idx) => {
                const last = idx === nodes.length - 1;
                return (
                    <Box key={`${node.label}-${idx}`} sx={{ display: "flex", gap: 1.5 }}>
                        {/* 좌측 레일: 원 + 연결선 */}
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Box sx={circleSx(node)}>
                                {circleIcon(node.status) || node.initial}
                            </Box>
                            {!last && (
                                <Box sx={{ width: 0, flexGrow: 1, minHeight: 26, my: 0.5, borderLeft: segBorder(node.status) }} />
                            )}
                        </Box>

                        {/* 우측 내용 */}
                        <Box sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 2.25 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }} noWrap>
                                    {node.label}
                                </Typography>
                                {node.isFinal && (
                                    <Box sx={{
                                        fontSize: 10, fontWeight: 700, color: "#b45309",
                                        bgcolor: "#fffbeb", border: "1px solid #fde68a",
                                        px: 0.6, py: 0.1, borderRadius: 1,
                                    }}>
                                        최종
                                    </Box>
                                )}
                            </Stack>

                            {node.sub && (
                                <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.1 }} noWrap>{node.sub}</Typography>
                            )}

                            <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: node.color }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: node.color }}>{node.subLabel}</Typography>
                                {node.actionAt && (
                                    <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>
                                        · {dayjs(node.actionAt).format("MM.DD HH:mm")}
                                    </Typography>
                                )}
                            </Stack>

                            {node.comment && (
                                <Box sx={{ mt: 0.8, px: 1.25, py: 0.75, bgcolor: "#f8fafc", border: "1px solid #eef2f6", borderRadius: 1.5 }}>
                                    <Typography sx={{ fontSize: 12, color: "#475569", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                        {node.comment}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

/* ── ViewerList — 열람자(공람) ─────────────────────────
 *
 * <b>이 문서를 누가 볼 수 있는지</b>만 알려주는 명단이다. 이름·직위·부서 세 칸짜리 표.
 *
 * <p>읽음 여부는 일부러 안 그린다. 서버는 읽은 시각을 갖고 있지만, 그걸 화면에 걸면 «왜 아직
 * 안 봤냐» 를 확인하는 자리가 되어 버린다 — 공람은 결재가 아니라 <b>공유</b>다. 결재선과 붙여
 * 놓되 위에 두는 것도 같은 이유다: 아래 결재 라인은 «처리해야 할 사람», 여기는 «보고 있는 사람».
 */
function ViewerList({ viewers = [] }) {
    return (
        <SectionCard title={`열람자 (${viewers.length})`} bodySx={{ p: 0 }}>
            {/* 표 스타일은 프리셋(ds.tableMiniSx)이 갖는다 — 화면은 정렬만 지정한다. */}
            <Table size="small" sx={ds.tableMiniSx}>
                <TableHead>
                    <TableRow>
                        <TableCell align="center">이름</TableCell>
                        <TableCell align="center">직위</TableCell>
                        <TableCell align="center">부서</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {viewers.map((v) => (
                        <TableRow key={v.employeeId}>
                            <TableCell align="center">{v.name}</TableCell>
                            <TableCell align="center">{v.position || "—"}</TableCell>
                            <TableCell align="center">{v.deptName || "—"}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </SectionCard>
    );
}

/* ── ApprovalDetail (default export) ──────────────────
 *
 * 결재 문서를 여는 <b>유일한 화면</b>이다. 결재 시스템·재무 연동·인사 연차 화면이 모두 이걸 쓴다.
 *
 * <p>예전엔 재무·인사용 상세({@code ApprovalIntegrationPanel} 안의 복제본)가 따로 있었다. 같은
 * 문서가 화면마다 다르게 보였고 — 한쪽은 신규 양식 문서가, 다른 쪽은 옛 문서가 빈 문서로 떴다 —
 * 댓글·열람자·수정처럼 한쪽에만 붙은 기능이 생겼다. 고칠 곳이 둘이면 반드시 한쪽만 고쳐진다.
 *
 * <p>화면별로 다른 건 <b>프롭 하나</b>다: {@code financeConfirm} 을 넘기면 최종 승인이
 * «지출확정 + 자금일보 반영» 이 된다. 그 외에는 전부 같은 규칙으로 그린다.
 */
export default function ApprovalDetail({
    approval, myId, onRefresh, onEdit, onClose,
    adminMode = false, onOpenApproval,
    financeConfirm, accounts = [],
}) {
    const [commentText, setCommentText] = useState("");
    const [actionDialog, setActionDialog] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [previewAtt, setPreviewAtt] = useState(null);
    const [confirmAccountId, setConfirmAccountId] = useState("");   // 재무 지출확정 시 자금일보 반영 계좌
    const fileInputRef = useRef();

    const isSubmitter    = approval.submitterId === myId;
    const myLine         = approval.lines?.find((l) => l.approverId === myId && (l.status === "PENDING" || l.status === "HELD"));
    const isMyPending    = myLine?.status === "PENDING";
    const isMyHeld       = myLine?.status === "HELD";
    /**
     * 기안자 본인은 자기 문서를 승인·반려할 수 없다.
     *
     * 예전엔 myLine(내가 결재자이고 PENDING/HELD) 만 보고 버튼을 그렸다. 받은결재함 쿼리는
     * submitter != approver 로 자기결재 문서를 걸러 주지만, 기안자는 «올린 결재함»·«전체» 탭에서
     * 자기 문서를 열 수 있고 거기서는 이 조건만으로 승인 버튼이 그대로 떴다 — 순수 UI 클릭 경로였다.
     * 서버가 막긴 하지만(assertNotSelfApproval → 403) 누를 수 있는 버튼을 두면 안 된다.
     *
     * 상신 시점 차단으로는 이미 만들어진 결재선을 못 고치므로, 기존 문서에서도 사라지려면 여기가 필요하다.
     * 아래 isConcluded(취소·수정 가능 여부)는 별개 개념이라 엮지 않는다.
     */
    /*
     * 기안자 본인이어도 «최종결재자» 자리면 승인·반려할 수 있다 (2026-08 결정).
     *
     * 그 유형의 결재권자가 본인인 사람은 자기 문서도 스스로 확정할 수 있어야 한다. 서버도 같은
     * 규칙이다(ApprovalAccessGuard.assertNotSelfApproval — 최종결재자면 통과).
     */
    const isFinalApprover = Boolean(
        approval.totalSteps > 0
        && approval.lines?.some((l) => l.stepOrder === approval.totalSteps && l.approverId === myId));
    const canApproveOrReject =
        !adminMode && (isMyPending || isMyHeld) && (!isSubmitter || isFinalApprover);
    // !isSubmitter 가 여기에도 있어야 한다 — 승인·반려만 가리면 기안자 화면엔 «보류» 하나만 남고,
    // 서버도 그 문은 열려 있어 막으려던 자기결재가 그대로 성립한다(서버는 assertNotSelfApproval 로 함께 닫았다).
    const canHold        = !adminMode && !isSubmitter && isMyPending;
    // 관리자 모드: 결재선 무관하게 강제 승인/거절 (미완료 상태일 때만)
    const canAdminAct    = adminMode && ["PENDING", "IN_PROGRESS", "HELD"].includes(approval.status);
    // 결론(승인·반려)이 나기 전까지는 기안자가 취소·수정할 수 있다. 중간 승인만으로는 원장·연차가
    // 생기지 않으므로 진행 중이어도 열어 둔다. 서버도 같은 조건이다(assertNotConcluded).
    const isConcluded    = approval.status === "APPROVED" || approval.status === "REJECTED";
    const canEdit        = isSubmitter && !isConcluded && approval.status !== "DRAFT";

    /**
     * 기안자의 «회수» — 결론이 나기 전까지 자기가 올린 것을 도로 거둬들인다.
     *
     * 예전엔 이 자리가 «결재 취소»(진짜 삭제)였다. 이름을 회수로 바꾸면서 동작도 회수로 맞췄다 —
     * 삭제는 문서가 통째로 사라져 «내가 올렸던 그 건» 을 확인할 데가 없고, 재기안의 출발점도
     * 없어진다. 회수는 발신함에 «취소» 로 남는다.
     */
    const canWithdrawAsSubmitter = isSubmitter && !isConcluded && approval.status !== "DRAFT";

    /**
     * 승인 취소(회수) — 최종 승인된 문서를 되돌린다.
     *
     * 계약이 엎어지는 경우가 실제로 있어서 열어 둔 자리다. 삭제가 아니라 회수인 이유는,
     * 최종 승인 시점에 만들어진 확정 원장·자금일보·연차 차감을 <b>되돌려야</b> 하기 때문이다.
     * 삭제로 지우면 그 보상이 돌지 않아 나가지 않은 돈이 원장에 남는다.
     *
     * 조건은 서버(assertCanWithdraw)와 같아야 한다 — 최종 승인 문서는 «마지막 단계 결재자»
     * 또는 전체 관리자만. 기안자는 여기서 제외된다(자기가 올린 걸 스스로 되돌리면 승인의 의미가 없다).
     * 진행 중인 문서의 회수는 서버가 기안자에게 열어 두고 있지만 버튼을 내지 않는다 —
     * 그 자리엔 이미 «삭제» 가 있어서, 둘을 나란히 두면 무엇이 다른지 매번 판단해야 한다.
     */
    /**
     * 진행 중 회수 — 최종결재자가 <b>자기 차례가 오기 전에</b> 멈추는 자리.
     *
     * 계약이 엎어졌다는 소식은 기안자보다 최종결재자가 먼저 아는 경우가 많은데, 결재자는 차례가
     * 와야 반려할 수 있어 그때까지 보고만 있어야 했다. 그동안 중간결재자들이 도장을 찍는 것도 낭비다.
     *
     * {@code !myLine} 을 붙여 <b>차례가 오면 버튼이 사라진다</b> — 그때는 반려가 맞는 도구다.
     * 반려는 «내 차례에 내가 판단했다» 는 기록이고, 회수는 «없던 일로» 다. 둘을 동시에 보여 주면
     * 매번 무엇을 눌러야 하는지 판단해야 한다.
     *
     * 기안자에게는 내지 않는다 — 진행 중 자기 문서는 위의 «결재 취소»(삭제)로 없앤다.
     */
    const isBeforeConclusion = ["PENDING", "IN_PROGRESS", "HELD"].includes(approval.status);
    // 결론이 난 문서는 승인·반려를 가리지 않는다. 버튼 이름이 «삭제» 인데 반려됐다는 이유로
    // 사라지면 «왜 어떤 건 지워지고 어떤 건 안 지워지지» 가 된다. 이미 취소된 건만 빠진다.
    const isConcludedDoc = approval.status === "APPROVED" || approval.status === "REJECTED";
    const canWithdraw =
        (isConcludedDoc && (isFinalApprover || adminMode))
        || (isBeforeConclusion && isFinalApprover && !myLine);

    const isFinanceApproval  = approval.type === "PAYMENT" || approval.type === "PURCHASE";
    const isFinalFinanceTurn = Boolean(isFinanceApproval && myLine && myLine.stepOrder === approval.totalSteps);
    /* 최종 승인이 «지출확정 + 자금일보 반영» 이 되는 자리. financeConfirm 을 넘긴 화면(재무 연동 탭)
       에서만 켜진다 — 결재 시스템에서는 계좌를 고를 맥락도, 계좌 목록도 없다. */
    const financeApprove = actionDialog === "approve" && isFinalFinanceTurn && typeof financeConfirm === "function";

    const typeName  = formLabel(approval);
    const typeColor = formColor(approval);

    /**
     * 수정 — 결재선이 처음부터 다시 도는 것을 반드시 먼저 알린다.
     *
     * 이미 승인한 사람의 승인이 사라지고 1단계부터 다시 받는다. 모르고 눌렀다가
     * "왜 승인이 초기화됐냐"가 되는 게 이 기능의 유일한 사고 지점이라, 확인을 한 번 받는다.
     */
    const handleEdit = async () => {
        const approvedCount = approval.lines?.filter((l) => l.status === "APPROVED").length || 0;
        const warn = approvedCount > 0
            ? `이미 ${approvedCount}명이 승인했습니다. 수정하면 그 승인이 모두 사라지고 결재선이 처음부터 다시 진행됩니다.`
            : "수정하면 결재선이 처음부터 다시 진행됩니다.";
        if (!(await confirmDialog({ message: `${warn}\n계속하시겠습니까?` }))) return;
        onEdit?.(approval);
    };

    const handleAction = () => runApiAction({
        request: () => {
            if (actionDialog === "approve") {
                // 재무 최종 승인은 결재 승인 API 가 아니라 지출확정 경로로 간다 — 원장·자금일보가 함께 움직인다.
                if (financeApprove)             return financeConfirm(approval.id, confirmAccountId || null);
                return adminMode ? approvalApi.adminApprove(approval.id, commentText) : approvalApi.approve(approval.id, commentText);
            }
            else if (actionDialog === "reject")  return adminMode ? approvalApi.adminReject(approval.id, commentText)  : approvalApi.reject(approval.id, commentText);
            else if (actionDialog === "withdraw") return approvalApi.withdraw(approval.id, commentText);
            else                                 return approvalApi.hold(approval.id, commentText);
        },
        successMsg: actionDialog === "approve" ? (adminMode ? "강제 승인했습니다." : "승인했습니다.")
            : actionDialog === "reject" ? (adminMode ? "강제 거절했습니다." : "반려했습니다.")
            : actionDialog === "withdraw"
                ? (canWithdrawAsSubmitter ? "회수했습니다." : "삭제했습니다. 원장·연차가 함께 원복됩니다.")
            : "보류했습니다.",
        errorMsg: (msg) => "처리 실패: " + msg,
        // onClose 를 넘긴 화면(모달 목록형)은 처리 후 닫는다 — 목록이 갱신되는데 모달만 옛 상태로 남는다.
        onSuccess: () => {
            setActionDialog(null); setCommentText(""); setConfirmAccountId("");
            onRefresh?.(); onClose?.();
        },
    });

    const handleFileUpload = async (files) => {
        await runApiAction({
            request: async () => {
                let lastResponse = null;
                for (const f of files) lastResponse = await approvalApi.uploadAttachment(approval.id, f);
                return lastResponse;
            },
            errorMsg: serverMsgOr("파일 업로드 실패"),
            successMsg: "파일이 첨부되었습니다.",
            setLoading: setUploading,
            onSuccess: onRefresh,
        });
    };

    const handleDeleteAttachment = async (id) => {
        if (!(await confirmDialog({ message: "첨부파일을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => approvalApi.deleteAttachment(id),
            errorMsg: serverMsgOr("삭제 실패"),
            onSuccess: onRefresh,
        });
    };

    return (
        <Box>
            {/* 문서 헤더 (아이콘 없음) */}
            <Box sx={{ pb: 2, mb: 2.5, borderBottom: "1px solid #eef2f6" }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={0.8} flexWrap="wrap">
                            <StatusBadge status={approval.status} />
                            <Chip label={typeName} size="small"
                                sx={{ fontSize: 11, height: 21, fontWeight: 700, bgcolor: `${typeColor}14`, color: typeColor }} />
                        </Stack>
                        <Typography sx={{ fontWeight: 800, fontSize: 22, color: "#0f172a", lineHeight: 1.3 }}>
                            {approval.title}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 0.5 }}>
                            {approval.submitterName} · {approval.submitterPosition}
                            {approval.submitterDept ? ` · ${approval.submitterDept}` : ""}
                            {" · "}{dayjs(approval.createdAt).format("YYYY-MM-DD HH:mm")}
                        </Typography>
                    </Box>
                    {/* 버튼은 한 묶음으로 오른쪽에 붙인다. 낱개로 두면 바깥 Stack 의
                        space-between 이 «수정» 을 제목과 «결재 취소» 사이에 흩뿌린다. */}
                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                        {canEdit && onEdit && (
                            <ActionButton variant="secondary"
                                startIcon={<EditRoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={handleEdit}
                                sx={{ fontSize: 11, py: 0.4 }}
                                label="수정" />
                        )}
                        {/* 기안자는 «회수» 다 — 자기가 올린 것을 도로 거둬들이는 행위라 문서가 남는다.
                            최종결재자의 «삭제»(아래)와 이름을 나눈 이유가 그것이다. */}
                        {canWithdrawAsSubmitter && (
                            <ActionButton variant="primary"
                                startIcon={<UndoRoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setActionDialog("withdraw")}
                                sx={{ fontSize: 11, py: 0.4, bgcolor: "#dc2626",
                                      "&:hover": { bgcolor: "#b91c1c" } }}
                                label="회수" />
                        )}
                    </Stack>


                </Stack>

                {/* 회수 사유 — 상태 배지만으로는 «왜 되돌렸는지» 를 알 수 없다. 계약 취소처럼
                    바깥 사정으로 원장이 지워진 건은 그 사정이 문서에 남아야 나중에 설명이 된다. */}
                {approval.withdrawnAt && (
                    <Box sx={{ mt: 1.5, p: 1.2, borderRadius: 1.5,
                               bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                            취소됨 · {approval.withdrawnByName || "알 수 없음"}
                            {" · "}{dayjs(approval.withdrawnAt).format("YYYY-MM-DD HH:mm")}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: "#334155", mt: 0.4, whiteSpace: "pre-wrap" }}>
                            {approval.withdrawReason || "사유가 기록되지 않았습니다."}
                        </Typography>
                    </Box>
                )}

            </Box>



            {/* 본문: 좌 결재 라인 / 우 내용+첨부(한 박스). 좌우 세로 길이 동일(stretch). */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "stretch", gap: 2.5 }}>


                {/* 우측: 내용 / 첨부 / 액션 */}
                <Box sx={{ flex: 1, minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* 재무 자동 반영 안내 */}


                    {/* 결재 내용 + 첨부파일 — 한 박스에서 구분선으로 분리 */}
                    <SectionCard title="결재 내용" bodySx={{ pt: 0.8, pb: 1.2 }} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        {/* 표 바깥 테두리 — 원본 결재 양식이 셀마다 실선으로 닫혀 있어 같은 인상을 준다. */}
                        <Box sx={{
                            border: "1px solid #e2e8f0", borderRadius: 1,
                            overflow: "hidden", mt: 0.4,
                        }}>
                            {/* fields 는 이 문서에 박힌 양식 버전의 정의다(서버가 상세 응답에 싣는다).
                                없으면 옛 유형별 렌더러로 떨어진다 — 개편 전 문서가 그대로 보여야 한다. */}
                            <FormFields type={approval.type} form={approval.form}
                                fields={approval.fields} values={approval.values}
                                onOpenApproval={onOpenApproval} applicant={approval.submitterName} />
                        </Box>

                        <Divider sx={{ mt: 1.6, mb: 1.1, borderColor: "#f1f5f9" }} />

                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#475569", letterSpacing: "0.01em" }}>
                                첨부파일 ({approval.attachments?.length || 0})
                            </Typography>
                            {approval.status !== "APPROVED" && approval.status !== "REJECTED" && (
                                <Tooltip title="파일 추가">
                                    <IconButton size="small" disabled={uploading} onClick={() => fileInputRef.current?.click()} sx={{ p: 0.4 }}>
                                        {uploading
                                            ? <CircularProgress size={14} />
                                            : <AttachFileRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />}
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                        <input ref={fileInputRef} type="file" hidden multiple
                            onChange={(e) => { handleFileUpload(Array.from(e.target.files)); e.target.value = ""; }} />

                        {approval.attachments?.length ? (
                            <Stack spacing={0.75}>
                                {approval.attachments.map((att) => (
                                    <Box key={att.id} sx={{
                                        display: "flex", alignItems: "center", gap: 1,
                                        p: "8px 10px", borderRadius: 1.5,
                                        border: "1px solid #e2e8f0", bgcolor: "#f8fafc",
                                    }}>
                                        <Box sx={{ fontSize: 20, display: "flex" }}>{fileIcon(att.contentType)}</Box>
                                        <Box flex={1} minWidth={0}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600 }} noWrap>{att.originalFileName}</Typography>
                                            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{formatSize(att.fileSize)}</Typography>
                                        </Box>
                                        {((att.contentType || "").startsWith("image/") || isPreviewable(att)) && (
                                            <Tooltip title="미리보기">
                                                <IconButton size="small" onClick={() => setPreviewAtt(att)} sx={{ p: 0.2 }}>
                                                    <ZoomInRoundedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Box component="a" href={att.fileUrl} target="_blank" rel="noreferrer" sx={{ display: "flex" }}>
                                            <DownloadRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                                        </Box>
                                        {att.uploadedAt && (
                                            <IconButton size="small" onClick={() => handleDeleteAttachment(att.id)} sx={{ p: 0.2 }}>
                                                <CloseRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>첨부파일 없음</Typography>
                        )}

                        {/* 댓글 — 결재선의 «사유» 와 다른 자리다. 사유는 결재자가 자기 차례에 한 번
                            남기는 이력이고, 댓글은 볼 수 있는 사람이면 누구나 언제든 남기는 의견이다.
                            («영수증 첨부 부탁드립니다» 같은 요청이 오가는 곳 — 양식 안내문구가
                            "댓글로 메모 남겨주세요" 라고 이 사용을 명시한다.) */}
                        <Divider sx={{ mt: 1.6, mb: 1.1, borderColor: "#f1f5f9" }} />
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#475569", mb: 1 }}>
                            댓글
                        </Typography>
                        <ApprovalComments approvalId={approval.id} />
                    </SectionCard>
                </Box>

                {/* 우측: 열람자 · 결재 라인 */}
                <Box sx={{ width: { xs: "100%", md: 300 }, flexShrink: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* 열람자(공람) — 결재 권한 없이 보기·댓글만 가능한 사람. 상신 화면에서 지정만 하고
                        상세에는 아무 데도 안 그려서, 지정한 사람도 지정된 사람도 확인할 방법이 없었다.
                        결재선과 <b>같은 칸에 섞지 않는다</b> — 섞으면 결재를 기다리는 사람으로 보인다. */}
                    {approval.viewers?.length > 0 && (
                        <ViewerList viewers={approval.viewers} />
                    )}

                    <SectionCard title="결재 라인">
                        <ApprovalLineRail lines={approval.lines} approval={approval} />
                    </SectionCard>
                </Box>

            </Box>

            {previewAtt && (
                <AttachmentPreviewModal open
                    attachment={(previewAtt.contentType || "").startsWith("image/")
                        ? { filename: previewAtt.originalFileName, mimeType: previewAtt.contentType, url: previewAtt.fileUrl }
                        : { id: previewAtt.id, filename: previewAtt.originalFileName, mimeType: previewAtt.contentType }}
                    loader={(previewAtt.contentType || "").startsWith("image/") ? undefined : () => approvalApi.previewAttachment(previewAtt.id)}
                    onClose={() => setPreviewAtt(null)} />
            )}

            {/* 승인/반려/보류 버튼 */}
            {(canApproveOrReject || canHold) && (
                <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                    {canApproveOrReject && <ActionButton variant="primary" startIcon={<CheckCircleRoundedIcon />}
                        onClick={() => setActionDialog("approve")}
                        sx={{ flex: 1, py: 0.9, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
                        label={isFinalFinanceTurn ? "결재 승인" : "승인"} />}
                    {canApproveOrReject && <ActionButton variant="primary" startIcon={<CancelRoundedIcon />}
                        onClick={() => setActionDialog("reject")}
                        sx={{ flex: 1, py: 0.9, bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } }}
                        label="반려" />}
                    {canHold && <ActionButton variant="secondary" startIcon={<PauseCircleRoundedIcon />}
                        onClick={() => setActionDialog("hold")}
                        sx={{ flex: 1, py: 0.9, color: "#d97706", borderColor: "#fcd34d",
                            "&:hover": { bgcolor: "#fffbeb", borderColor: "#d97706" } }}
                        label="보류" />}
                </Stack>
            )}

            {/* 삭제 — 결재 자체를 없앤다. 최종결재자·관리자만. 되돌릴 수 없는 자리라 채움 빨강이다
                (외곽선형은 «해도 되는 일» 처럼 보인다). */}
            {canWithdraw && (
                <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                    <ActionButton variant="primary" startIcon={<UndoRoundedIcon />}
                        onClick={() => setActionDialog("withdraw")}
                        sx={{ flex: 1, py: 0.9, bgcolor: "#dc2626",
                            "&:hover": { bgcolor: "#b91c1c" } }}
                        label="삭제" />
                </Stack>
            )}

            {/* 관리자 강제 승인/거절 — 결재선 무시 */}
            {canAdminAct && (
                <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                    <ActionButton variant="primary" startIcon={<CheckCircleRoundedIcon />}
                        onClick={() => setActionDialog("approve")}
                        sx={{ flex: 1, py: 0.9, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
                        label="강제 승인" />
                    <ActionButton variant="primary" startIcon={<CancelRoundedIcon />}
                        onClick={() => setActionDialog("reject")}
                        sx={{ flex: 1, py: 0.9, bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } }}
                        label="강제 거절" />
                </Stack>
            )}

            {/* 액션 확인 다이얼로그 */}
            <Dialog open={Boolean(actionDialog)} onClose={() => setActionDialog(null)} maxWidth="xs" fullWidth>
                <Box sx={{ p: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>
                        {actionDialog === "approve"
                            ? isFinalFinanceTurn ? "결재 승인 및 재무 반영" : "승인"
                            : actionDialog === "reject" ? "반려"
                            : actionDialog === "withdraw"
                                ? (canWithdrawAsSubmitter ? "회수" : "삭제")
                            : "⏸ 보류"}{" "}하시겠습니까?
                    </Typography>

                    {/* 경고 문구는 상태에 따라 다르다 — 진행 중 문서에는 되돌릴 원장·연차가 아직 없다.
                        없는 위험을 적어 두면 정작 승인 건에서 읽어야 할 경고가 같은 무게로 흘러간다. */}
                    {actionDialog === "withdraw" && (
                        <Typography sx={{ fontSize: 12, color: "#9a3412", bgcolor: "#fff7ed",
                            border: "1px solid #fed7aa", borderRadius: 1.5, p: 1, mb: 1.5 }}>
                            {canWithdrawAsSubmitter
                                ? "올린 결재를 도로 거둬들입니다. 결재자에게 알림이 가고, 문서는 «취소» 로 "
                                  + "남아 나중에 다시 올릴 때 출발점이 됩니다."
                                : approval.status === "APPROVED"
                                ? "이 결재로 만들어진 확정 원장·자금일보·연차 차감이 함께 원복됩니다. "
                                  + "결재함에서 사라지고 기안자·결재자·열람자에게 알림이 갑니다. "
                                  + "사유를 적어 주세요 — 나중에 원장이 왜 지워졌는지 설명할 수 있는 유일한 기록입니다."
                                : approval.status === "REJECTED"
                                ? "반려된 결재를 없앱니다. 결재함에서 사라지고 기안자·결재자에게 알림이 갑니다. "
                                  + "누가 반려했는지는 결재선에 그대로 남습니다."
                                : "결재가 여기서 멈추고 진행 중인 결재함에서 사라집니다. "
                                  + "기안자와 결재자에게 알림이 가므로, 왜 멈추는지 사유를 적어 주세요."}
                        </Typography>
                    )}

                    {actionDialog === "approve" && isFinalFinanceTurn && (
                        <Typography sx={{ fontSize: 12, color: "#166534", bgcolor: "#f0fdf4",
                            border: "1px solid #bbf7d0", borderRadius: 1.5, p: 1, mb: 1.5 }}>
                            지출확정하면 완료(실적)로 등록됩니다{financeApprove ? " · 아래 계좌를 고르면 자금일보에도 자동 반영됩니다." : "."}
                        </Typography>
                    )}

                    {financeApprove && (
                        <Box sx={{ mb: 1.5 }}>
                            <FormField label="입출금 계좌 (자금일보 자동 반영)">
                                <ComboBox placeholder="계좌 선택 (선택 안 하면 자금일보 미반영)"
                                    options={accounts.map((a) => ({ value: a.id, label: a.alias || a.bankName }))}
                                    value={confirmAccountId}
                                    onChange={(v) => setConfirmAccountId(v)} />
                            </FormField>
                        </Box>
                    )}

                    <TextField fullWidth multiline minRows={2} size="small"
                        placeholder={actionDialog === "withdraw" ? "취소 사유 (예: 계약 취소)" : "의견 (선택)"}
                        value={commentText} onChange={(e) => setCommentText(e.target.value)}/>

                    <Stack direction="row" spacing={1} mt={2} justifyContent="flex-end">
                        <ActionButton variant="cancel" onClick={() => setActionDialog(null)} />
                        {/* 확인 버튼은 actionDialog 종류별로 다른 색을 가져야 해서 sx override (승인=초록, 반려=빨강, 보류=주황). */}
                        <ActionButton variant="confirm" onClick={handleAction} startIcon={false}/>
                    </Stack>
                </Box>
            </Dialog>
        </Box>
    );
}
